// intentClassifier — 2-stegs klassificering: keyword först, LLM fallback.
//
// Prioritetsordning i keyword-matcher:
//   1. meta.* (moderation-frågor, historik-frågor — överlappar Batch BBB)
//   2. revert (tydligast signal)
//   3. refactor
//   4. extend (MÅSTE köras före modify — 'lägg till' > 'ändra')
//   5. modify_in_place
//   6. explain
//   7. build_new (lägst prioritet — fångar resten när något bygg-keyword finns)
//
// Returnerar null om inget keyword-pattern matchar — caller kör då LLM-fallback
// (classifyByLlm) eller använder DEFAULT_INTENT.

import {
  type Intent,
  type IntentClassification,
  DEFAULT_INTENT,
} from './intentTypes';
import { isMetaQuery } from './moderationPipeline';

interface KeywordRule {
  intent: Intent;
  pattern: RegExp;
  /** Base-confidence när mönstret träffar (0-1). */
  confidence: number;
}

// Ordning är viktig — första träff vinner.
// OBS: `\b` i JS regex är ASCII-only. Svenska åäö räknas INTE som word-chars,
// så `\bångra\b` failar. Vi använder (?:^|\s|[.,!?]) lookbehind/ahead för
// prompter som innehåller svenska, och behåller \b för rena ASCII-termer.
const SW_BOUNDARY_START = '(?:^|[\\s.,!?])';
const SW_BOUNDARY_END = '(?=$|[\\s.,!?])';

const KEYWORD_RULES: readonly KeywordRule[] = [
  // Revert — extremt tydlig signal
  {
    intent: 'revert',
    confidence: 0.95,
    pattern: new RegExp(
      `(\\brevert\\b|${SW_BOUNDARY_START}(återgå|ångra|rulla tillbaka|gå tillbaka till)${SW_BOUNDARY_END}|\\brollback\\b|\\bundo\\b)`,
      'i',
    ),
  },

  // Refactor — specifikt ord
  {
    intent: 'refactor',
    confidence: 0.85,
    pattern: new RegExp(
      `(\\brefactor\\b|${SW_BOUNDARY_START}(refaktorera|städa (koden|flödet)|omstrukturera)${SW_BOUNDARY_END}|\\brestructure\\b|\\bclean up\\b)`,
      'i',
    ),
  },

  // Extend — MÅSTE komma före modify eftersom 'bygg vidare' är starkast signal
  {
    intent: 'extend',
    confidence: 0.9,
    pattern: new RegExp(
      `(${SW_BOUNDARY_START}(bygg\\s+vidare|lägg(\\s+till)?|utöka|expandera|komplettera)${SW_BOUNDARY_END}|\\bbuild\\s+on\\b|\\bextend\\b|\\badd\\s+(to|more|another)\\b)`,
      'i',
    ),
  },

  // Modify in place — ändrar befintligt
  {
    intent: 'modify_in_place',
    confidence: 0.8,
    pattern: new RegExp(
      `(${SW_BOUNDARY_START}(ändra|uppdatera|ersätt|byt\\s+ut|fix(a)?|korrigera)${SW_BOUNDARY_END}|\\bmodify\\b|\\bchange\\b|\\bupdate\\b|\\breplace\\b)`,
      'i',
    ),
  },

  // Explain — inga ändringar ska göras
  {
    intent: 'explain',
    confidence: 0.9,
    pattern: new RegExp(
      `(${SW_BOUNDARY_START}(förklara|vad\\s+gör|hur\\s+fungerar|beskriv)${SW_BOUNDARY_END}|\\bexplain\\b|\\bwhat\\s+does\\b|\\bdescribe\\b)`,
      'i',
    ),
  },

  // Build new — lägst prioritet, fångar build-signals utan 'vidare'
  {
    intent: 'build_new',
    confidence: 0.8,
    pattern: new RegExp(
      `(${SW_BOUNDARY_START}(bygg|skapa|generera|ny(tt)?)${SW_BOUNDARY_END}|\\b(make|create|build|new)\\b).{0,30}(${SW_BOUNDARY_START}(flöde|flöden|process|diagram)${SW_BOUNDARY_END}|\\b(flow|workflow|chart|design)\\b)`,
      'i',
    ),
  },
];

/**
 * Snabb regel-baserad klassificering. Returnerar null om inget mönster
 * matchade — caller bör då prova LLM-fallback.
 */
export function classifyByKeywords(
  prompt: string,
): IntentClassification | null {
  // Steg 1: Meta-frågor har alltid högsta prioritet (återanvänder moderation-
  // pipelinens isMetaQuery för single source of truth).
  if (isMetaQuery(prompt)) {
    // Skilj på moderation vs history — enkel heuristik
    const isHistory = /\b(mina|min|my)\b[^.!?]*\b(chattar|historik|history|conversations|flöden|tidigare)\b/i.test(prompt);
    return {
      intent: isHistory ? 'meta.history' : 'meta.moderation',
      confidence: 0.95,
      source: 'keyword',
      reasoning: 'Meta-question detected by moderation pipeline',
      matchedPattern: 'meta-bypass',
    };
  }

  // Steg 2: Kör keyword-rules och detektera ambiguity. Om MULTIPLA
  // regler matchar → prompten är tvetydig och confidence reduceras så
  // AiAssistantDialog visar IntentConfirmationBanner (I-105). Första
  // träffen (högst prio i KEYWORD_RULES-ordningen) är fortfarande det
  // valda intent:et, men user får chansen att ändra innan AI-call.
  const matches: Array<{ rule: (typeof KEYWORD_RULES)[number]; matched: string }> = [];
  for (const rule of KEYWORD_RULES) {
    const match = prompt.match(rule.pattern);
    if (match) matches.push({ rule, matched: match[0].slice(0, 200) });
  }
  if (matches.length === 0) return null;
  const primary = matches[0];
  if (matches.length >= 2) {
    const otherIntents = matches.slice(1).map((m) => m.rule.intent).join(', ');
    return {
      intent: primary.rule.intent,
      // Ambiguity-penalty: dra ner till max 0.6 så banner triggers
      // (threshold 0.7). Bevarar primary-intent som default men user får välja.
      confidence: Math.min(primary.rule.confidence, 0.6),
      source: 'keyword',
      reasoning: `Ambiguous — also matched: ${otherIntents}`,
      matchedPattern: primary.matched,
    };
  }
  return {
    intent: primary.rule.intent,
    confidence: primary.rule.confidence,
    source: 'keyword',
    matchedPattern: primary.matched,
  };
}

/**
 * LLM-fallback för tvetydiga prompts. Skickar en kort klassificerings-
 * prompt till samma LLM som används för chat. Returnerar alltid en
 * classification (fallback till DEFAULT_INTENT vid fel).
 *
 * Used av AiAssistantDialog när classifyByKeywords returnerar null eller
 * låg confidence. Vi passar in endpoint + API key från dialogens konfig.
 */
export interface LlmClassifierConfig {
  endpoint: string;
  apiKey?: string;
  model?: string;
  signal?: AbortSignal;
}

const LLM_CLASSIFIER_SYSTEM_PROMPT = `You are an intent classifier for a workflow-builder AI assistant.

Given a user prompt, classify it as EXACTLY ONE of these intents:
- build_new: user wants a fresh new workflow from scratch
- extend: user wants to add ON TOP of an existing workflow (preserving existing parts)
- modify_in_place: user wants to change something existing (may remove/replace)
- refactor: restructure without behavior change
- revert: go back to a previous version
- explain: just explain, no canvas changes
- meta.moderation: question about warnings/flags/strikes in the system itself
- meta.history: question about user's own history/conversations

Return ONLY a compact JSON object:
{"intent": "extend", "confidence": 0.85, "reasoning": "user said 'bygg vidare' which means extend"}

confidence: 0-1. Use >=0.8 only when very sure. Use 0.5-0.7 for ambiguous.
reasoning: one short sentence.`;

export async function classifyByLlm(
  prompt: string,
  config: LlmClassifierConfig,
  historyContext?: string,
): Promise<IntentClassification> {
  try {
    const userContent = historyContext
      ? `Recent context:\n${historyContext}\n\nCurrent prompt:\n${prompt}`
      : prompt;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (config.apiKey) headers.Authorization = `Bearer ${config.apiKey}`;

    const res = await fetch(config.endpoint, {
      method: 'POST',
      headers,
      signal: config.signal,
      body: JSON.stringify({
        model: config.model ?? 'google/gemini-3-flash-preview',
        messages: [
          { role: 'system', content: LLM_CLASSIFIER_SYSTEM_PROMPT },
          { role: 'user', content: userContent },
        ],
        response_format: { type: 'json_object' },
        temperature: 0,
        max_tokens: 150,
      }),
    });

    if (!res.ok) throw new Error(`classifier HTTP ${res.status}`);

    const json = (await res.json()) as {
      choices?: Array<{ message?: { content?: string } }>;
    };
    const content = json.choices?.[0]?.message?.content;
    if (!content) throw new Error('empty classifier response');

    const parsed = JSON.parse(content) as {
      intent?: string;
      confidence?: number;
      reasoning?: string;
    };

    const validIntent = parsed.intent && isValidIntent(parsed.intent)
      ? (parsed.intent as Intent)
      : DEFAULT_INTENT;

    return {
      intent: validIntent,
      confidence: clampConfidence(parsed.confidence ?? 0.5),
      source: 'llm',
      reasoning: parsed.reasoning,
    };
  } catch (e) {
    // LLM-fel får inte blockera chat — degradera tyst till default.
    return {
      intent: DEFAULT_INTENT,
      confidence: 0.3,
      source: 'llm',
      reasoning: e instanceof Error ? `classifier-error: ${e.message}` : 'classifier-error',
    };
  }
}

function isValidIntent(s: string): boolean {
  return [
    'build_new', 'extend', 'modify_in_place', 'refactor', 'revert',
    'explain', 'meta.moderation', 'meta.history',
  ].includes(s);
}

function clampConfidence(c: number): number {
  if (!Number.isFinite(c)) return 0.5;
  return Math.max(0, Math.min(1, c));
}

/**
 * Main entrypoint: klassificera prompt. Kör keyword först, LLM som fallback.
 * När dropdown-val finns i caller ska den skippa detta helt (manual).
 */
export async function classifyIntent(
  prompt: string,
  llmConfig?: LlmClassifierConfig,
  historyContext?: string,
): Promise<IntentClassification> {
  const kw = classifyByKeywords(prompt);
  if (kw) return kw;
  if (llmConfig) {
    return classifyByLlm(prompt, llmConfig, historyContext);
  }
  return {
    intent: DEFAULT_INTENT,
    confidence: 0.3,
    source: 'keyword',
    reasoning: 'no-keyword-match, no-llm-config',
  };
}
